import { useEffect } from "react";
import { useDataChangeEffect, watchSlices } from "../../lib/useDataChangeEffect.js";
import {
  useAuthStore,
  useCalendarsStore,
  useLabelsStore,
  useSyncStore,
  useTasksStore,
  useTopicsStore,
} from "../../stores/index.js";

const DEBOUNCE_MS = 2500; // coalesce a burst of local edits into one push
const TICK_MS = 3 * 60 * 1000; // periodic pull of other devices' changes

/** Watched data slices — any change schedules a debounced outbound sync. They
 *  are watched IMPERATIVELY: selecting them into render would re-render Shell
 *  (and the whole routed tree) on every edit — see `useDataChangeEffect`. */
const SYNCED_DATA = [
  watchSlices(useTasksStore, (s) => [s.tasks, s.rules, s.overrides]),
  watchSlices(useLabelsStore, (s) => [s.labels]),
  watchSlices(useTopicsStore, (s) => [s.topics]),
  watchSlices(useCalendarsStore, (s) => [s.calendars]),
];

/**
 * Background multi-device sync. When signed in with a server URL, it syncs:
 *  - shortly after any local data change (debounced — outbound push),
 *  - on a periodic tick + on window focus + on coming back online (inbound pull),
 *  - and once on mount/sign-in (boot).
 * All auto-runs are `silent` (no success/error toasts; the Sync card still shows
 * status). `syncNow` guards against overlap and only rehydrates on real changes,
 * so its own writes don't loop back through this effect. Mount once, in Shell.
 */
export function useSyncReconciler(): void {
  const accessToken = useAuthStore((s) => s.accessToken);
  const authLoaded = useAuthStore((s) => s.loaded);
  const authLoad = useAuthStore((s) => s.load);

  const url = useSyncStore((s) => s.url);
  const syncLoaded = useSyncStore((s) => s.loaded);
  const syncLoad = useSyncStore((s) => s.load);
  const syncNow = useSyncStore((s) => s.syncNow);

  useEffect(() => {
    if (!authLoaded) void authLoad();
    if (!syncLoaded) void syncLoad();
  }, [authLoaded, authLoad, syncLoaded, syncLoad]);

  const enabled = Boolean(url && accessToken);

  // Debounced sync on local change (and an initial run when first enabled).
  useDataChangeEffect(SYNCED_DATA, () => void syncNow({ silent: true }), {
    debounceMs: DEBOUNCE_MS,
    enabled,
    runOnEnable: true,
  });

  // Periodic pull + sync on focus/online (catches other devices' changes).
  useEffect(() => {
    if (!enabled) return;
    const run = () => void syncNow({ silent: true });
    const id = setInterval(run, TICK_MS);
    window.addEventListener("focus", run);
    window.addEventListener("online", run);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", run);
      window.removeEventListener("online", run);
    };
  }, [enabled, syncNow]);
}
